import { Fab } from "@rmwc/fab";
import { TextField } from "@rmwc/textfield";
import PropTypes from "prop-types";
import { useEffect } from "react";

import '@rmwc/textfield/styles';
import "./QuantiteSelector.css";

// #region CONSTANTES
const ADD = "add";
const NUMB = "numb";
const SUB = "sub";
// #endregion

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher un TextField controllé par des boutons + et -
 * @category QuantiteSelector
 */
function QuantiteSelector(props) {
  //#region INITIALISATION
  // Récupération des props
  const {
    doShowButtons,
    onChange,
    validQuantites,
    value
  } = props;

  const numValue = typeof value === "number" ? value : parseInt(value, 10) || 0;
  // #endregion

  // #region EVENTS
  const getNextValid = (current) => {
    if (!validQuantites?.length) return current + 1;
    const next = validQuantites.find(q => q > current);
    return next !== undefined ? next : current;
  };

  const getPrevValid = (current) => {
    if (!validQuantites?.length) return Math.max(0, current - 1);
    const filtered = validQuantites.filter(q => q < current);
    return filtered.length > 0 ? filtered[filtered.length - 1] : 0;
  };

  const handleChange = (type, numberValue) => {
    let newValue = numValue;

    switch (type) {
      case ADD:
        newValue = getNextValid(numValue);
        break;

      case NUMB:
        newValue = typeof numberValue === "number" ? numberValue : parseInt(numberValue, 10) || 0;
        if (validQuantites?.length && newValue > 0) {
          const nearest = validQuantites.find(q => q >= newValue) ?? validQuantites[validQuantites.length - 1];
          const nearestPrev = validQuantites.filter(q => q <= newValue).pop();
          newValue = nearestPrev !== undefined && (newValue - nearestPrev) < (nearest - newValue) ? nearestPrev : nearest;
        }
        break;

      case SUB:
        newValue = getPrevValid(numValue);
        break;

      default:
        break;
    }
    onChange(newValue);
  };
  // #endregion

  // #region HOOK D'EFFET
  useEffect(() => {
    if (value) return;
    const initial = validQuantites?.length ? validQuantites[0] : 1;
    onChange(initial);
  }, [onChange, value]);
  // #endregion

  // #region INTERFACE
  return (
    <div className="QuantiteSelector-Container">
      {doShowButtons &&
        <Fab
          icon="remove"
          mini
          onClick={() => { handleChange(SUB) }}
        />
      }
      <TextField
        className="QuantiteSelector-TextField"
        disabled
        onChange={(evt) => handleChange(NUMB, evt.target.value)}
        style={(String(numValue).length > 5) ? { width: "6rem" } : { width: "5rem" }}
        type="number"
        value={numValue}
      />
      {doShowButtons &&
        <Fab
          icon="add"
          mini
          onClick={() => handleChange(ADD)}
        />
      }
    </div>
  );
  // #endregion
};
// #endregion

// #region PROPRIETES
/**
 * Type des propriétés de {@link QuantiteSelector}
 * @typedef {Object} QuantiteSelector.propTypes
 * @property {boolean} [doShowButtons=true] True si les boutons doivent être affiché, false sinon
 * @property {function} onChange Fonction callback sur modification de la donnée
 * @property {number | string} [value] Valeur à afficher dans l'input
*/
QuantiteSelector.propTypes = {
  doShowButtons: PropTypes.bool.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  validQuantites: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired
};

// Valeurs par défaut des propriétés
QuantiteSelector.defaultProps = {
  doShowButtons: true,
  value: 1
}
// #endregion

export { QuantiteSelector };

